
import React, { useState, useEffect } from 'react';
import { format } from "date-fns";
import { hu } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Clock, X } from "lucide-react";
import { TimePicker } from "@/components/ui/time-picker";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { UseFormReturn } from 'react-hook-form';
import { EventFormValues } from '../../schemas/eventFormSchema';

interface DateTimeSelectorProps {
  form: UseFormReturn<EventFormValues>;
  startDateName: 'startDate';
  endDateName: 'endDate';
  startTimeName: 'startTime';
  endTimeName: 'endTime';
  isMultiDay: boolean;
  onMultiDayChange: (isMultiDay: boolean) => void;
  hasTime: boolean;
  onHasTimeChange: (hasTime: boolean) => void;
}

const DateTimeSelector = ({
  form,
  startDateName,
  endDateName,
  startTimeName,
  endTimeName,
  isMultiDay,
  onMultiDayChange,
  hasTime,
  onHasTimeChange
}: DateTimeSelectorProps) => {
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  
  // Add default months state based on current field values
  const [startDateMonth, setStartDateMonth] = useState<Date>(form.getValues(startDateName) || new Date());
  const [endDateMonth, setEndDateMonth] = useState<Date>(form.getValues(endDateName) || new Date());
  
  // Update calendar month when field values change
  useEffect(() => {
    const startDate = form.getValues(startDateName);
    if (startDate) {
      setStartDateMonth(startDate);
    }
    
    const endDate = form.getValues(endDateName);
    if (endDate) {
      setEndDateMonth(endDate);
    }
  }, [form, startDateName, endDateName]);
  
  // Update calendar month when popovers open
  useEffect(() => {
    if (startDateOpen) {
      const startDate = form.getValues(startDateName);
      if (startDate) {
        setStartDateMonth(startDate);
      }
    }
    
    if (endDateOpen) {
      const endDate = form.getValues(endDateName);
      if (endDate) {
        setEndDateMonth(endDate);
      }
    }
  }, [startDateOpen, endDateOpen, form, startDateName, endDateName]);
  
  // Handle start date change
  const handleStartDateChange = (date: Date | undefined) => {
    if (!date) return;
    
    form.setValue(startDateName, date, { shouldDirty: true });
    
    // If not a multi-day event, set end date to the same day
    if (!isMultiDay) {
      form.setValue(endDateName, date, { shouldDirty: true });
    } else {
      // For multi-day events, set end date to start date + 1 day
      const newEndDate = new Date(date);
      newEndDate.setDate(newEndDate.getDate() + 1);
      form.setValue(endDateName, newEndDate, { shouldDirty: true });
      setEndDateMonth(newEndDate);
    }
    
    setStartDateOpen(false);
  };
  
  // Handle end date change
  const handleEndDateChange = (date: Date | undefined) => {
    if (!date) return;
    form.setValue(endDateName, date, { shouldDirty: true });
    setEndDateOpen(false);
  };
  
  // Handle time toggle
  const handleTimeToggle = () => {
    onHasTimeChange(!hasTime);
    
    // When enabling time, set default times
    if (!hasTime) {
      const now = new Date();
      const startTime = new Date();
      startTime.setHours(now.getHours());
      startTime.setMinutes(0);
      
      const endTime = new Date();
      endTime.setHours(now.getHours() + 1);
      endTime.setMinutes(0);
      
      form.setValue(startTimeName, startTime, { shouldDirty: true });
      form.setValue(endTimeName, endTime, { shouldDirty: true });
    }
  };
  
  // Handle multi-day toggle
  const handleMultiDayToggle = () => {
    const newIsMultiDay = !isMultiDay;
    onMultiDayChange(newIsMultiDay);
    
    if (newIsMultiDay) {
      // Set end date to start date + 1 day
      const startDate = form.getValues(startDateName);
      const newEndDate = new Date(startDate);
      newEndDate.setDate(newEndDate.getDate() + 1);
      form.setValue(endDateName, newEndDate, { shouldDirty: true });
      setEndDateMonth(newEndDate);
    } else {
      // Set end date equal to start date
      const startDate = form.getValues(startDateName);
      form.setValue(endDateName, startDate, { shouldDirty: true });
      setEndDateMonth(startDate);
    }
  };
  
  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-4">
        {/* Start date field */}
        <FormField
          control={form.control}
          name={startDateName}
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Kezdés dátuma</FormLabel>
              <div className="flex items-center gap-2">
                <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "flex-1 pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "yyyy. MMMM d.", { locale: hu })
                        ) : (
                          <span>Válasszon dátumot</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={handleStartDateChange}
                      disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                      locale={hu}
                      month={startDateMonth}
                      onMonthChange={setStartDateMonth}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                
                <Button
                  type="button"
                  variant={isMultiDay ? "default" : "outline"}
                  size="sm"
                  onClick={handleMultiDayToggle}
                  className="whitespace-nowrap"
                >
                  {isMultiDay ? "Egynapos" : "Többnapos"}
                </Button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        
        {/* End date field - only for multi-day events */}
        {isMultiDay && (
          <FormField
            control={form.control}
            name={endDateName}
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Befejezés dátuma</FormLabel>
                <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "yyyy. MMMM d.", { locale: hu })
                        ) : (
                          <span>Válasszon dátumot</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={handleEndDateChange}
                      disabled={(date) => {
                        const startDate = form.getValues(startDateName);
                        // End date must be on or after start date
                        return date < startDate;
                      }}
                      locale={hu}
                      month={endDateMonth}
                      onMonthChange={setEndDateMonth}
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        
        {/* Time specification toggle */}
        <div className="flex justify-between items-center">
          <Button
            type="button"
            variant={hasTime ? "default" : "outline"}
            size="sm"
            onClick={handleTimeToggle}
            className="gap-2"
          >
            <Clock className="h-4 w-4" />
            {hasTime ? "Időpont eltávolítása" : "Időpont megadása"}
          </Button>
        </div>
        
        {/* Time fields - only if time is enabled */}
        {hasTime && (
          <div className={isMultiDay ? "space-y-4" : "flex gap-4"}>
            <FormField
              control={form.control}
              name={startTimeName}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Kezdés időpontja</FormLabel>
                  <FormControl>
                    <TimePicker
                      value={field.value}
                      onChange={field.onChange}
                      interval={15}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name={endTimeName}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Befejezés időpontja</FormLabel>
                  <FormControl>
                    <TimePicker
                      value={field.value}
                      onChange={field.onChange}
                      interval={15}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DateTimeSelector;
